import { NavigationClient, type NavigationOptions } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useEffect } from "react";
import { type NavigateFunction, useNavigate } from "react-router";
import { getAzureAdConfig } from "../config";
import { saveOriginForLoginHost } from "./loginHostOrigin";

/**
 * This class ensures MSAL uses React Router for navigation
 * instead of full page reloads.
 */
export class CustomNavigationClient extends NavigationClient {
  private navigate: NavigateFunction;
  private loginHostUri: string | null;

  constructor(navigate: NavigateFunction, loginHostUri: string | null) {
    super();
    this.navigate = navigate;
    this.loginHostUri = loginHostUri;
  }

  async navigateExternal(url: string, options: NavigationOptions): Promise<boolean> {
    if (this.loginHostUri) {
      await saveOriginForLoginHost(this.loginHostUri);
    }
    return super.navigateExternal(url, options);
  }

  /**
   * Navigates to other pages within the same application
   */
  async navigateInternal(url: string, options: NavigationOptions): Promise<boolean> {
    const relativePath = url.replace(window.location.origin, "");
    if (options.noHistory) {
      this.navigate(relativePath, { replace: true });
    } else {
      this.navigate(relativePath);
    }
    return false; // Prevent MSAL from internal navigation
  }
}

/**
 * Integrated component to handle MSAL-Router connection and User State synchronization.
 * It must be rendered inside <BrowserRouter> to have access to 'navigate'.
 */
export const MsalIntegration = () => {
  const navigate = useNavigate();
  const { instance } = useMsal();

  // Navigation configuration
  useEffect(() => {
    const navigationClient = new CustomNavigationClient(navigate, getAzureAdConfig().redirectUri);
    instance.setNavigationClient(navigationClient);
  }, [navigate, instance]);

  return null;
};
